import React, { useCallback, useContext, useEffect, useState, useRef } from "react";
import { ProctoringRoute } from "routes/route";
import { useNavigate } from "react-router-dom";
import Button from "components/Button";
import FooterLogo from "components/FooterLogo";
import Header from "components/Header";
import IdCard from "assets/img/id_card.png";
import Loader from "assets/img/loader_green.gif";
import VideoPlayer from "components/VideoPlayer";
import { FormattedMessage } from "react-intl";
import { useAppSelector, useAppDispatch } from "store/store";
import { GlobalAppState } from "globals/interfaces";
import { nextScreen } from "routes/workflow";
import {
  ClientErrorTypes,
  ErrorMessage,
  PhotoIdFileSource,
  ScreenShareStatus
} from "globals/enums";

// Components
import Canvas from "components/Canvas";
// Helpers
import { alertHelper } from "helpers/alert-helper";
import { AlertName } from "helpers/alert-type";
import { getBase64 } from "helpers/file-helper";
import { AlertLogConfig } from "services/session-token";
import {
  faceDetectionModelUrl,
  mediapipeWasmUrl,
  objectDetectionModelUrl,
  orgIdsWithFaceDetection,
  photoIdFaceDetectionConfidence,
  photoIdMaxFileSize,
  photoIdPersonScoreThreshold
} from "config/constant";
import { handleError } from "helpers/sentry";
import { FaceDetector, FilesetResolver, ObjectDetector } from "@mediapipe/tasks-vision";
import { isWebGLSupported } from "helpers/face-detection";

import Base from "components/layouts/Base";
import Content from "components/layouts/Content";
import { MediaContext } from "context/MediaContext";
import { usePhotoIdCapture, photoIdCaptureUploadApi } from "services/photoIdCaptureUpload";
import { parseOrgIds } from "helpers/common";
import { guardedStepNavigation } from "helpers/guarded-step-navigation";
import { isFaceDetectionEligible } from "helpers/face-detection-eligibility";

const PhotoIdVerification = (): React.ReactElement | null => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const globalState: GlobalAppState = useAppSelector((state) => state.app);
  const screenShareStatus = useAppSelector((state) => state.screenShare.screenShareStatus);
  const screenShareStatusRef = useRef<ScreenShareStatus | "">(screenShareStatus);

  useEffect(() => {
    screenShareStatusRef.current = screenShareStatus;
  }, [screenShareStatus]);

  const alertConfig: AlertLogConfig[] = useAppSelector((state) => state.app.alertConfigs);
  const [{ fileSource, file, validationError }, setPhotoVerification] = useState({
    fileSource: "",
    file: "",
    validationError: false
  });
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const { mediaStream } = useContext(MediaContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isActiveRef = useRef(true);
  const faceDetectorRef = useRef<FaceDetector | null>(null);
  const objectDetectorRef = useRef<ObjectDetector | null>(null);
  const visionRef = useRef<Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>> | null>(null);
  const [faceDetectionError, setFaceDetectionError] = useState<ErrorMessage | null>(null);
  const [
    photoIdCapture,
    { isUninitialized: photoIdUninitialized, isLoading: photoIdLoading, isError: photoIdError }
  ] = usePhotoIdCapture();

  useEffect(() => {
    isActiveRef.current = true;
    return () => {
      isActiveRef.current = false;
      faceDetectorRef.current?.close();
      faceDetectorRef.current = null;
      objectDetectorRef.current?.close();
      objectDetectorRef.current = null;
      visionRef.current = null;
    };
  }, []);

  const proceedToNextScreen = useCallback(() => {
    const getNextScreen = nextScreen(
      ProctoringRoute.PhotoIDVerification,
      globalState.configuration,
      globalState.session.session_type
    );
    guardedStepNavigation({
      sourceRoute: ProctoringRoute.PhotoIDVerification,
      destinationRoute: getNextScreen,
      screenShareStatus: screenShareStatusRef.current,
      dispatch,
      navigate
    });
  }, [globalState.configuration, globalState.session.session_type, dispatch, navigate]);

  const handleResetApiState = () => {
    if (!photoIdUninitialized) {
      dispatch(photoIdCaptureUploadApi.util.resetApiState());
    }
  };

  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };

  const uploadClick = () => {
    uploadPhotoId();
  };

  const handleOnCapture = () => {
    setFaceDetectionError(null);
    setPhotoVerification((prevState) => ({
      ...prevState,
      fileSource: PhotoIdFileSource.ImageCaptured,
      file: "",
      validationError: false
    }));
    handleResetApiState();
  };

  const handleOnRecapture = () => {
    setFaceDetectionError(null);
    setPhotoVerification((prevState) => ({ ...prevState, fileSource: "" }));
    handleResetApiState();
  };

  const getPhotoCanvas = async (): Promise<HTMLCanvasElement | null> => {
    if (!file) return canvasRef.current;

    const image = new Image();
    image.src = file;
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("Failed to load the selected photo ID image"));
    });

    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth || image.width;
    canvas.height = image.naturalHeight || image.height;
    canvas.getContext("2d")?.drawImage(image, 0, 0);
    return canvas;
  };

  const validatePhotoFace = async (photoCanvas: HTMLCanvasElement) => {
    if (!visionRef.current) {
      const vision = await FilesetResolver.forVisionTasks(mediapipeWasmUrl);
      if (!isActiveRef.current) return null;
      visionRef.current = vision;
    }

    const delegate = isWebGLSupported() ? "GPU" : "CPU";
    if (!faceDetectorRef.current) {
      const faceDetector = await FaceDetector.createFromOptions(visionRef.current, {
        baseOptions: { modelAssetPath: faceDetectionModelUrl, delegate },
        runningMode: "IMAGE",
        minDetectionConfidence: photoIdFaceDetectionConfidence,
        minSuppressionThreshold: 0.3
      });
      if (!isActiveRef.current) {
        faceDetector.close();
        return null;
      }
      faceDetectorRef.current = faceDetector;
    }

    if (!isActiveRef.current) return null;
    const faceCount = faceDetectorRef.current.detect(photoCanvas).detections.length;
    if (faceCount === 0) return ErrorMessage.NoFaceDetected;

    if (!objectDetectorRef.current) {
      const objectDetector = await ObjectDetector.createFromOptions(visionRef.current, {
        baseOptions: { modelAssetPath: objectDetectionModelUrl, delegate },
        runningMode: "IMAGE",
        scoreThreshold: photoIdPersonScoreThreshold,
        categoryAllowlist: ["person"]
      });
      if (!isActiveRef.current) {
        objectDetector.close();
        return null;
      }
      objectDetectorRef.current = objectDetector;
    }

    if (!isActiveRef.current) return null;
    return objectDetectorRef.current.detect(photoCanvas).detections.length > 0
      ? null
      : ErrorMessage.NoPersonDetected;
  };

  const uploadPhotoId = async () => {
    const sessionType = globalState?.session?.session_type;
    let payload = canvasRef.current?.toDataURL("image/png");
    if (file) {
      payload = file;
    }

    try {
      const configuredOrganizationIds = parseOrgIds(orgIdsWithFaceDetection);
      const detectionEligible = isFaceDetectionEligible(
        sessionType,
        globalState.organizationId,
        configuredOrganizationIds
      );
      if (detectionEligible) {
        const photoCanvas = await getPhotoCanvas();
        if (!photoCanvas) throw new Error("Photo ID image is unavailable");
        if (!isActiveRef.current) return;

        let detectionError: ErrorMessage | null = null;
        try {
          detectionError = await validatePhotoFace(photoCanvas);
        } catch (error) {
          if (!isActiveRef.current) return;
          handleError(
            error instanceof Error ? error : new Error(String(error)),
            ClientErrorTypes.PhotoIdUploadError
          );
        }
        if (!isActiveRef.current) return;
        if (detectionError) {
          setFaceDetectionError(detectionError);
          return;
        }
      }

      setFaceDetectionError(null);
      const alertData = alertHelper(alertConfig).getAlert(AlertName.PhotoIdCapture, payload, null);
      await photoIdCapture(alertData).unwrap();
      if (!isActiveRef.current) return;
      alertHelper(alertConfig).raiseAlert(AlertName.IdCardCaptureSuccess, dispatch);
      proceedToNextScreen();
    } catch (e: unknown) {
      if (!isActiveRef.current) return;
      const errorMessage =
        typeof e === "string"
          ? e
          : e instanceof Error
          ? e.message
          : typeof e === "object" && e !== null && "message" in e
          ? e.message
          : undefined;
      if (errorMessage === ErrorMessage.MultipleFacesDetected) {
        handleResetApiState();
        alertHelper(alertConfig).raiseAlert(AlertName.IdCardCaptureSuccess, dispatch);
        proceedToNextScreen();
        return;
      }
      if (
        errorMessage === ErrorMessage.NoFaceDetected &&
        !isFaceDetectionEligible(
          sessionType,
          globalState.organizationId,
          parseOrgIds(orgIdsWithFaceDetection)
        )
      ) {
        handleResetApiState();
        alertHelper(alertConfig).raiseAlert(AlertName.IdCardCaptureSuccess, dispatch);
        proceedToNextScreen();
        return;
      }
      handleError(
        e instanceof Error ? e : new Error(String(e)),
        ClientErrorTypes.PhotoIdUploadError
      );
      alertHelper(alertConfig).raiseAlert(AlertName.IdDCardCaptureFailure, dispatch);
      if (isActiveRef.current) {
        setPhotoVerification((prevState) => ({ ...prevState, fileSource: "", file: "" }));
      }
      setFaceDetectionError(null);
    }
  };

  const renderError = () => {
    if (validationError) {
      return (
        <div className="tvp-clr-red tvp-buttonPart" aria-live="polite">
          <FormattedMessage id="page.photoIdCapture.file.upload.error" />
        </div>
      );
    }
    return (
      <div className="tvp-clr-red tvp-buttonPart" aria-live="polite">
        <FormattedMessage id="error.nofaceDetected" />
      </div>
    );
  };

  const onFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileSelected = event?.target?.files![0];
    if (fileSelected) {
      try {
        handleResetApiState();
        setPhotoVerification((prevState) => ({
          ...prevState,
          fileSource: PhotoIdFileSource.IdCardUpload,
          validationError: false
        }));
        setFaceDetectionError(null);

        const fileSizeInKB = fileSelected.size / 1024;
        if (fileSizeInKB > photoIdMaxFileSize) {
          setPhotoVerification((prevState) => ({ ...prevState, validationError: true }));
          return;
        }

        const fileBase64 = await getBase64(fileSelected);
        if (!fileBase64 || typeof fileBase64 !== "string") {
          throw new Error("Failed to convert file to base64 string");
        }

        setPhotoVerification((prevState) => ({ ...prevState, file: fileBase64 }));
      } catch (error) {
        handleError(error, ClientErrorTypes.PhotoIdUploadError);
        setPhotoVerification((prevState) => ({
          ...prevState,
          validationError: true,
          file: ""
        }));
      }
    }
  };

  return (
    <Base>
      <Header
        title={<FormattedMessage id="page.photoIdCapture.title" />}
        titleClass={"tvp-text-sm tvp-font-semibold tvp-clr-neutral tvp-uppercase tvp-ml-2.5"}
        tabIndex={1}
        aria-label="Photo ID Capture Header"
      />
      <Content aria-label="Photo ID Verification Content">
        <div
          className="tvp-justify-between tvp-px-2 tvp-flex  tvp-w-full tvp-h-auto tvp-bg-white  tvp-my-4 tvp-flex-grow tvp-col-row "
          role="region"
          aria-label="Photo ID Capture Section"
        >
          <div className="tvp-flex tvp-flex-col tvp-mt-3 tvp-mb-3 tvp-photo-verification-page tvp-justify-center">
            {!mediaStream && (
              <img
                className="tvp-miniLoader"
                src={Loader}
                alt="Loading media stream"
                role="status"
                aria-live="polite"
              />
            )}
            {mediaStream && (
              <>
                <div className="tvp-mb-4 tvp-text-xs tvp-phto-text-folder">
                  <FormattedMessage
                    id="page.photoIdCapture.instruction"
                    values={{ strong: (chunks) => <strong>{chunks}</strong> }}
                  />
                </div>
                <div className="tvp-mb-4 tvp-photIdVideoPlayerWrapper">
                  {mediaStream && (
                    <VideoPlayer
                      videoRef={videoRef}
                      mediaStream={mediaStream}
                      playerWidth={"200px"}
                      aria-label="Live camera feed"
                    />
                  )}
                  {fileSource === PhotoIdFileSource.ImageCaptured && (
                    <Canvas
                      videoRef={videoRef}
                      canvasRef={canvasRef}
                      height={150}
                      width={200}
                      aria-label="Captured photo preview"
                    />
                  )}
                </div>
                <div className="tvp-buttonPart">
                  <span className="tvp-flex tvp-flex-col tvp-items-center">
                    {(!fileSource || fileSource === PhotoIdFileSource.IdCardUpload) && (
                      <Button
                        label={<FormattedMessage id="button.capture" />}
                        tabIndex={2}
                        aria-label="Capture photo from camera"
                        aria-disabled={photoIdLoading}
                        className={
                          photoIdLoading
                            ? "tvp-flex tvp-justify-center tvp-items-center tvp-py-1 tvp-px-5 tvp-rounded tvp-w-auto tvp-border-solid tvp-border-2 tvp-min-w-150 tvp-cursor-pointer tvp-bg-lgray tvp-clr-dgray tvp-mx-auto tvp-bdr-lgray"
                            : "tvp-flex tvp-justify-center tvp-items-center tvp-py-1 tvp-px-5 tvp-rounded tvp-w-auto tvp-border-solid tvp-border-2 tvp-min-w-150 tvp-cursor-pointer tvp-clr-white tvp-mx-auto tvp-bg-green tvp-bdr-green"
                        }
                        onClick={handleOnCapture}
                      />
                    )}
                    {fileSource === PhotoIdFileSource.ImageCaptured && (
                      <Button
                        label={<FormattedMessage id="button.reCapture" />}
                        tabIndex={2}
                        aria-label="Recapture Photo ID"
                        aria-disabled={photoIdLoading}
                        className={
                          photoIdLoading
                            ? "tvp-flex tvp-justify-center tvp-items-center tvp-py-1 tvp-px-5 tvp-rounded tvp-w-auto tvp-border-solid tvp-border-2 tvp-min-w-150 tvp-cursor-pointer tvp-bg-lgray tvp-clr-dgray tvp-mx-auto tvp-bdr-lgray"
                            : "tvp-flex tvp-justify-center tvp-items-center tvp-py-1 tvp-px-5 tvp-rounded tvp-w-auto tvp-border-solid tvp-border-2 tvp-min-w-150 tvp-cursor-pointer tvp-clr-white tvp-mx-auto tvp-bg-red tvp-bdr-red tvp-w-150 tvp-m-auto"
                        }
                        onClick={handleOnRecapture}
                        disabled={photoIdLoading}
                      />
                    )}
                  </span>
                </div>
              </>
            )}
          </div>
          <div className="tvp-mt-3 tvp-divider-identity">
            <span className="tvp-dividerText-identity">
              <FormattedMessage id="page.photoid.divider" />
            </span>
          </div>

          <div className="tvp-flex tvp-flex-col tvp-mt-3 tvp-photo-verification-page">
            <div className="tvp-mb-4 tvp-text-xs tvp-phto-text-folder">
              <FormattedMessage
                id="page.photoIdCapture.upload.instruction"
                values={{ strong: (chunks) => <strong>{chunks}</strong> }}
              />
              <p>
                <FormattedMessage
                  id="page.photoIdCapture.filesize.instruction"
                  values={{ strong: (chunks) => <strong>{chunks}</strong> }}
                />
              </p>
            </div>
            <div className="tvp-mb-4 tvp-photIdVideoPlayerWrapper">
              {file && (
                <img
                  className="tvp-IdPreview"
                  src={file}
                  alt="Preview of the selected Photo ID"
                  aria-label="Selected photo preview"
                />
              )}
              {!file && (
                <img
                  className="tvp-mt-4 IdCard"
                  src={IdCard}
                  alt="Default Photo ID icon"
                  aria-label="Default photo ID placeholder"
                />
              )}
            </div>
            <label className="tvp-cursor-pointer" htmlFor="fileUpload">
              <input
                className="tvp-custom-file-input"
                id="fileUpload"
                type="file"
                onChange={onFileSelected}
                onClick={(event: any) => {
                  event.target.value = null;
                }}
                placeholder="Select photo id file"
                ref={fileInputRef}
                accept=".png,.jpg,.jpeg"
                style={{ display: "none" }}
                aria-label="Select a photo ID file to upload"
              />
              <Button
                label={<FormattedMessage id="button.selectFile" />}
                tabIndex={3}
                className={
                  photoIdLoading
                    ? "tvp-flex tvp-justify-center tvp-items-center tvp-py-1 tvp-px-5 tvp-rounded tvp-w-auto tvp-border-solid tvp-border-2 tvp-min-w-150 tvp-cursor-pointer tvp-bg-lgray tvp-clr-dgray tvp-mx-auto tvp-bdr-lgray"
                    : "tvp-flex tvp-justify-center tvp-items-center tvp-py-1 tvp-px-5 tvp-rounded tvp-w-auto tvp-border-solid tvp-border-2 tvp-min-w-150 tvp-cursor-pointer tvp-clr-white tvp-mx-auto tvp-bg-green tvp-bdr-green"
                }
                onClick={handleSelectFile}
                disabled={fileSource ? photoIdLoading : false}
                aria-disabled={fileSource ? photoIdLoading : false}
              />
            </label>
          </div>
        </div>
        {(photoIdError || validationError || faceDetectionError) &&
          (faceDetectionError ? (
            <div className="tvp-clr-red tvp-buttonPart" aria-live="polite">
              <FormattedMessage id="error.nofaceDetected" />
            </div>
          ) : (
            renderError()
          ))}
        <div className="tvp-buttonPart">
          <Button
            tabIndex={4}
            label={<FormattedMessage id="page.photoIdCapture.upload" />}
            className={
              !fileSource || photoIdLoading || photoIdError || validationError
                ? "tvp-flex tvp-justify-center tvp-items-center tvp-py-1 tvp-px-5 tvp-rounded tvp-w-auto tvp-border-solid tvp-border-2 tvp-min-w-150 tvp-cursor-pointer tvp-bg-lgray tvp-clr-dgray tvp-mx-auto tvp-bdr-lgray"
                : "tvp-flex tvp-justify-center tvp-items-center tvp-py-1 tvp-px-5 tvp-rounded tvp-w-auto tvp-border-solid tvp-border-2 tvp-min-w-150 tvp-cursor-pointer tvp-clr-white tvp-mx-auto tvp-bg-green tvp-bdr-green"
            }
            onClick={fileSource ? uploadClick : () => {}}
            disabled={!fileSource || photoIdLoading || photoIdError || validationError}
            aria-disabled={!fileSource || photoIdLoading || photoIdError || validationError}
            aria-label="Upload the selected photo ID file"
          />
        </div>
      </Content>
      <FooterLogo />
    </Base>
  );
};
export default PhotoIdVerification;
